import { cn } from '@/lib/utils';
import { LoadingPage } from '@/libs/components/src/lib/LoadingPage';
import { Button, ButtonColors } from '@chiroup/components/Button';
import {
  JointsSelected,
  SpineParts,
  SpinePartsSelected,
  SpineSide,
} from '@chiroup/core/functions/bodyData';
import React, { useEffect, useMemo, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { JointRegions, Regions } from '../../../../../constants';
import JointRegion from './JointRegion';

const TAB_OPTIONS: { value: JointRegions; text: string }[] = [
  { value: JointRegions.HEAD, text: 'Head' },
  { value: JointRegions.SPINE, text: 'Spine' },
  { value: JointRegions.RIBS, text: 'Ribs' },
  { value: JointRegions.S, text: 'Shoulder' },
  { value: JointRegions.E, text: 'Elbow' },
  { value: JointRegions['H/W'], text: 'Hand and Wrist' },
  { value: JointRegions.H, text: 'Hip and Pelvis' },
  { value: JointRegions.K, text: 'Knee' },
  { value: JointRegions['F/A'], text: 'Foot and Ankle' },
  { value: JointRegions.O, text: 'Other' },
];

const jointsInRegion: {
  [key in JointRegions]?: string[];
} = {
  [JointRegions.HEAD]: ['Atlantooccipital', 'Temporomandibular'],
  [JointRegions.SPINE]: ['Spine'],
  [JointRegions.RIBS]: [
    '1st costovertebral',
    '2nd costovertebral',
    '3rd costovertebral',
    '4th costovertebral',
    '5th costovertebral',
    '6th costovertebral',
    '7th costovertebral',
    '8th costovertebral',
    '9th costovertebral',
    '10th costovertebral',
    '11th costovertebral',
    '12th costovertebral',
    '1st costosternal',
    '2nd costosternal',
    '3rd costosternal',
    '4th costosternal',
    '5th costosternal',
    '6th costosternal',
    '7th costosternal',
  ],
  [Regions.S]: ['Acromioclavicular', 'Sternoclavicular', 'Glenohumeral', 'Scapulothoracic'],
  [Regions.E]: ['Ulnohumeral', 'Radiohumeral', 'Proximal radioulnar'],
  [Regions['H/W']]: [
    'Radiocarpal',
    'Carpometacarpal (1-5)',
    'Metacarpophalangeal (1-5)',
    'Interphalangeal (1-5)',
    'Ulnocarpal',
    'Distal radioulnar',
    'Intercarpal',
  ],
  [Regions.H]: ['Sacroiliac', 'Sacrococcygeal', 'Coccygeal', 'Pubic symphysis', 'Acetabular'],
  [Regions.K]: ['Tibiofemoral', 'Patellofemoral', 'Proximal tibiofibular'],
  [Regions['F/A']]: [
    'Subtalar ',
    'Intertarsal',
    'Tarsometatarsal (1-5)',
    'Metatarsophalangeal (1-5)',
    'Interphalangeal (1-5)',
    'Distal tibiofibular',
    'Talocrural',
    'Calcaneocuboid',
    'Talonavicular',
  ],
};

type Props = {
  close?: () => void;
  value: {
    [key: string]: JointsSelected | SpinePartsSelected;
  };
  onChange: (tab: string, key: string, val: { value?: number; pain?: boolean }) => void;
  onChangeSpine?: (part: SpineParts, value?: SpineSide, amount?: number) => void;
  onRemoveRegion: (region: string) => void;
  isFetching?: boolean;
  signed?: boolean;
  /** Hides Remove region + Leg Length Inequality (trial SOAP sandbox). */
  hideRegionControls?: boolean;
  /** Trial SOAP sandbox: called instead of switching when any region other
   *  than the current one is chosen (a full-EHR pop-up opens). Below sm it
   *  also collapses the region <select> to a compact "Region: <name>" chip
   *  (tap opens the same pop-up), freeing screen space for the picker. */
  interceptRegionSwitch?: () => void;
};

const Joints: React.FC<Props> = ({
  close,
  value,
  onChange,
  onChangeSpine,
  onRemoveRegion,
  isFetching,
  signed,
  hideRegionControls,
  interceptRegionSwitch,
}) => {
  const [currentTab, setCurrentTab] = useState<JointRegions>();

  const possibleTabs = useMemo(() => {
    return TAB_OPTIONS.filter(region => !!jointsInRegion[region.value]).map(region => ({
      name: region.text,
      region: region.value,
    }));
  }, []);

  const tabs = useMemo(() => {
    const tabsInUse = possibleTabs.filter(region => !!value?.[region.region]);

    if (tabsInUse?.length) {
      return tabsInUse;
    }

    // For v1 encounters, always show Spine tab as default when no data exists
    // This ensures spine joint selection is always accessible
    const spineTab = possibleTabs.find(tab => tab.region === JointRegions.SPINE);

    return spineTab ? [spineTab] : [];
  }, [possibleTabs, value]);

  useEffect(() => {
    if (!currentTab) {
      if (tabs?.length) {
        setCurrentTab(tabs[0]?.region);
      } else {
        setCurrentTab(JointRegions.SPINE);
      }
    }
  }, [possibleTabs, tabs, currentTab]);

  const tabsLeft = useMemo(() => {
    const tabRegionsInUse = tabs.map(tab => tab.region);

    return possibleTabs.filter(
      region => !value?.[region.region] && !tabRegionsInUse.includes(region.region)
    );
  }, [possibleTabs, value, tabs]);

  const removeRegion = () => {
    onRemoveRegion(currentTab as string);
    setCurrentTab(tabs.find(tab => tab.region !== currentTab)?.region || JointRegions.SPINE);
  };

  return (
    <div className="flex flex-col">
      {isFetching ? (
        <LoadingPage />
      ) : (
        <div className="overflow-x-auto p-4 rounded-t-lg">
          <Scrollbars className="min-h-14 w-full" autoHide>
            <div>
              <div className="sm:hidden">
                {interceptRegionSwitch ? (
                  <button
                    type="button"
                    onClick={interceptRegionSwitch}
                    className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400"
                  >
                    Region:
                    <span className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1 font-medium text-gray-700 dark:border-dark-gray-600 dark:bg-dark-gray-800 dark:text-gray-200">
                      {possibleTabs.find(tab => tab.region === currentTab)?.name || 'Spine'}
                      <span aria-hidden>▾</span>
                    </span>
                  </button>
                ) : (
                  <>
                    <label htmlFor="tabs" className="sr-only">
                      Select a tab
                    </label>
                    <select
                      id="tabs"
                      name="tabs"
                      className="block w-full rounded-md border-gray-300 focus:border-primary-500 focus:ring-primary-500"
                      onChange={e => {
                        setCurrentTab(e.target.value as JointRegions);
                      }}
                      value={currentTab || ''}
                    >
                      {possibleTabs.map(tab => (
                        <option key={tab.name} value={tab.region}>
                          {tab.name}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>
              <div className="hidden sm:block">
                <nav className="flex space-x-4" aria-label="Tabs">
                  {(tabs || []).map(tab => (
                    <div
                      key={tab.name}
                      className={cn(
                        currentTab === tab.region
                          ? 'bg-white dark:bg-dark-gray-900 border-gray-300 dark:border-dark-gray-700 text-gray-700 dark:text-dark-gray-50'
                          : 'text-gray-500 hover:text-gray-700 dark:text-dark-gray-50 dark:hover:text-dark-gray-100 border-transparent',
                        'px-3 py-2 font-medium text-sm rounded-md cursor-pointer whitespace-nowrap border'
                      )}
                      aria-current={currentTab === tab.region ? 'page' : undefined}
                      onClick={() => {
                        if (interceptRegionSwitch && tab.region !== currentTab) {
                          interceptRegionSwitch();

                          return;
                        }
                        setCurrentTab(tab.region);
                      }}
                    >
                      {tab.name}
                    </div>
                  ))}
                  {(tabsLeft || []).map(tab => (
                    <div
                      key={tab.name}
                      className={cn(
                        currentTab === tab.region
                          ? 'bg-gray-100 text-gray-700 dark:bg-dark-gray-800 dark:text-dark-gray-100'
                          : 'bg-gray-100 text-gray-300 hover:text-gray-400 dark:bg-dark-gray-700 dark:text-dark-gray-500',
                        'px-3 py-2 font-medium text-sm rounded-md cursor-pointer whitespace-nowrap border border-transparent'
                      )}
                      aria-current={currentTab === tab.region ? 'page' : undefined}
                      onClick={() => {
                        if (interceptRegionSwitch && tab.region !== currentTab) {
                          interceptRegionSwitch();

                          return;
                        }
                        setCurrentTab(tab.region);
                      }}
                    >
                      {tab.name}
                    </div>
                  ))}
                </nav>
              </div>
            </div>
          </Scrollbars>
        </div>
      )}
      {currentTab && !isFetching ? (
        <JointRegion
          joints={jointsInRegion[currentTab]}
          onChange={onChange}
          onChangeSpine={onChangeSpine}
          currentTab={currentTab}
          val={value[currentTab] || []}
          signed={signed}
          hideLegLengthInequality={hideRegionControls}
        />
      ) : null}
      {!isFetching && (
        <div className="flex flex-row justify-between p-4">
          {!hideRegionControls && (
            <Button
              onClick={removeRegion}
              text="Remove region"
              color={ButtonColors.plainWithBorder}
              className="mr-5 sm:mr-0"
              disabled={signed}
            />
          )}
          {!!close && <Button onClick={close} text="Close" color={ButtonColors.plainWithBorder} />}
        </div>
      )}
    </div>
  );
};

export default Joints;
